import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Autocomplete from "@mui/material/Autocomplete";
import { useDataProvider, useNotify, usePermissions, useRecordContext } from "react-admin";

import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import type { PermissionRecord } from "@/types/permissions";

import {
  EMPLOYEE_LEAVE_NOTIFY_CONTACTS_LOCATION,
  EMPTY_MANUAL_LEAVE_NOTIFY_CONTACT_FORM,
  extractLeaveNotifyContactRows,
  validateManualLeaveNotifyContactForm,
  type LeaveNotifyContactRow,
  type ManualLeaveNotifyContactFormValues,
} from "../leave-notify-contacts-form";

type ItsRow = {
  ITS_ID?: string;
  Full_Name?: string | null;
  Mobile?: string | null;
  WhatsApp_No?: string | null;
};

function itsRowLabel(r: ItsRow): string {
  const id = String(r.ITS_ID ?? "").trim();
  const name = String(r.Full_Name ?? "").trim();
  return id ? `${id} — ${name}` : name || id;
}

function apiErrorMessage(err: unknown, fallback: string): string {
  const body = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data;
  const msg = body?.message;
  if (Array.isArray(msg)) return msg.join(", ");
  if (typeof msg === "string" && msg.trim()) return msg;
  return fallback;
}

/**
 * Staff Edit: list who gets looped in via WhatsApp (info only, not approvers) when this staff
 * member requests leave and when that leave is approved. Search the ITS directory first; when
 * someone isn't in it, add them by hand (name / ITS number / WhatsApp number).
 */
export function StaffLeaveNotifyContactsSection() {
  const record = useRecordContext<{ id: number }>();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions<PermissionRecord>();
  const canWrite = hasPermission(permissions, "employees.edit");

  const employeeId = record?.id != null ? Number(record.id) : 0;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<LeaveNotifyContactRow[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [searchOptions, setSearchOptions] = useState<ItsRow[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState<ManualLeaveNotifyContactFormValues>(
    EMPTY_MANUAL_LEAVE_NOTIFY_CONTACT_FORM
  );

  const fetchRows = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const { data } = await callApi({
        location: EMPLOYEE_LEAVE_NOTIFY_CONTACTS_LOCATION,
        method: "GET",
        data: { employeeId, limit: 100 },
      });
      setRows(extractLeaveNotifyContactRows(data));
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to load leave notify contacts"), { type: "error" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId, notify]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    const q = searchInput.trim();
    if (!q) {
      setSearchOptions([]);
      return undefined;
    }
    let cancelled = false;
    setSearchLoading(true);
    const t = window.setTimeout(() => {
      void dataProvider
        .getList("itsdata", {
          pagination: { page: 1, perPage: 20 },
          sort: { field: "updatedAt", order: "DESC" },
          filter: { q },
        })
        .then(({ data }) => {
          if (!cancelled) setSearchOptions(data as ItsRow[]);
        })
        .catch(() => {
          if (!cancelled) setSearchOptions([]);
        })
        .finally(() => {
          if (!cancelled) setSearchLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [dataProvider, searchInput]);

  const addFromDirectory = async (row: ItsRow) => {
    if (!canWrite || !employeeId) return;
    setSubmitting(true);
    try {
      await callApi({
        location: EMPLOYEE_LEAVE_NOTIFY_CONTACTS_LOCATION,
        method: "POST",
        data: {
          employeeId,
          itsId: row.ITS_ID,
          name: row.Full_Name ?? row.ITS_ID ?? "",
          whatsappNumber: row.WhatsApp_No || row.Mobile || "",
        },
      });
      notify("Contact tagged", { type: "success" });
      setSearchInput("");
      setSearchOptions([]);
      await fetchRows();
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to tag contact"), { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const addManually = async () => {
    const validated = validateManualLeaveNotifyContactForm(employeeId, manualForm);
    if (!validated.ok) {
      notify(validated.error, { type: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      await callApi({
        location: EMPLOYEE_LEAVE_NOTIFY_CONTACTS_LOCATION,
        method: "POST",
        data: validated.payload,
      });
      notify("Contact tagged", { type: "success" });
      setManualForm(EMPTY_MANUAL_LEAVE_NOTIFY_CONTACT_FORM);
      setShowManualForm(false);
      await fetchRows();
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to tag contact"), { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const removeContact = async (id: string) => {
    setSubmitting(true);
    try {
      await callApi({
        location: EMPLOYEE_LEAVE_NOTIFY_CONTACTS_LOCATION,
        method: "DELETE",
        id,
      });
      notify("Contact removed", { type: "success" });
      await fetchRows();
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to remove contact"), { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!record) return null;

  return (
    <Box sx={{ mt: 2, width: "100%" }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
        Notify on leave (WhatsApp)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        These people are messaged for information only — when this staff member requests leave and
        when that leave is approved. They cannot approve or decline.
      </Typography>

      {!canWrite ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          You can view notify contacts but cannot change them.
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No one is tagged yet.
        </Typography>
      ) : (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>WhatsApp number</TableCell>
              <TableCell>Source</TableCell>
              {canWrite ? <TableCell align="right" /> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.whatsappNumber}</TableCell>
                <TableCell>
                  <Chip
                    label={row.source === "DIRECTORY" ? "Directory" : "Manual"}
                    size="small"
                    color={row.source === "DIRECTORY" ? "info" : "default"}
                    variant="outlined"
                  />
                </TableCell>
                {canWrite ? (
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label="Remove contact"
                      disabled={submitting}
                      onClick={() => void removeContact(row.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {canWrite ? (
        <Stack spacing={1.5}>
          <Autocomplete<ItsRow, false, false, false>
            options={searchOptions}
            loading={searchLoading}
            inputValue={searchInput}
            onInputChange={(_, v) => setSearchInput(v)}
            onChange={(_, v) => {
              if (v) void addFromDirectory(v);
            }}
            value={null}
            getOptionLabel={(o) => itsRowLabel(o)}
            filterOptions={(opts) => opts}
            disabled={submitting}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search ITS directory"
                placeholder="Name or ITS number"
                size="small"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Button
            size="small"
            onClick={() => setShowManualForm((v) => !v)}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            {showManualForm ? "Cancel manual entry" : "Can't find them? Add manually"}
          </Button>

          {showManualForm ? (
            <Stack spacing={1.5} sx={{ pl: 1, borderLeft: 2, borderColor: "divider" }}>
              <TextField
                label="Name"
                value={manualForm.name}
                onChange={(e) => setManualForm((prev) => ({ ...prev, name: e.target.value }))}
                size="small"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="ITS number (optional)"
                value={manualForm.itsId}
                onChange={(e) => setManualForm((prev) => ({ ...prev, itsId: e.target.value }))}
                size="small"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="WhatsApp number"
                value={manualForm.whatsappNumber}
                onChange={(e) =>
                  setManualForm((prev) => ({ ...prev, whatsappNumber: e.target.value }))
                }
                size="small"
                fullWidth
                disabled={submitting}
              />
              <Button
                variant="contained"
                onClick={() => void addManually()}
                disabled={submitting}
                sx={{ alignSelf: "flex-start" }}
              >
                {submitting ? "Adding…" : "Add manually"}
              </Button>
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}
